import React, { useEffect, useState } from 'react';
import { employeeAdminAPI, companiesAPI } from '../services/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, UserCog } from 'lucide-react';

const ROLES = [['employee', 'Employee'], ['manager', 'Manager'], ['payroll_admin', 'Payroll Admin'], ['hr_admin', 'HR Admin'], ['super_admin', 'Super Admin']];
const STATUSES = ['active', 'on_leave', 'on_hold', 'resigned', 'terminated', 'transferred', 'retired', 'absconded', 'deceased'];
const EMP_TYPES = ['permanent', 'contract', 'probation', 'intern', 'part_time', 'consultant'];

const withCurrent = (list, cur) => (cur && !list.includes(cur) ? [cur, ...list] : list);

const SECTIONS = [
  ['Identity & Login', [['employee_code', 'Employee ID *'], ['first_name', 'First Name *'], ['last_name', 'Last Name *'], ['email', 'Login / Work Email *', 'email']]],
  ['Employment', [['department', 'Department'], ['designation', 'Designation'], ['branch_location', 'Branch / Location'], ['join_date', 'Joining Date *', 'date'], ['probation_end_date', 'Probation End', 'date']]],
  ['Personal & Compliance', [['phone', 'Phone'], ['personal_email', 'Personal Email', 'email'], ['date_of_birth', 'Date of Birth', 'date'], ['gender', 'Gender'], ['nationality', 'Nationality'], ['national_id', 'National ID'], ['passport_number', 'Passport No.'], ['passport_expiry', 'Passport Expiry', 'date'], ['visa_number', 'Visa No.'], ['visa_expiry', 'Visa Expiry', 'date'], ['work_permit_expiry', 'Work Permit Expiry', 'date']]],
];
const ALL_FIELDS = [...SECTIONS.flatMap(([, f]) => f.map(([k]) => k)), 'company_id', 'employment_type', 'status'];

const PickField = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const FullEditEmployeeDialog = ({ open, onOpenChange, employee, onSuccess }) => {
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [account, setAccount] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employee) return;
    setLoading(true);
    Promise.all([employeeAdminAPI.getFull(employee.id), companiesAPI.getAll()])
      .then(([r, c]) => {
        const e = r.data.employee;
        const init = Object.fromEntries(ALL_FIELDS.map(k => [k, e[k] ?? '']));
        init.role = r.data.user_account?.role || '';
        setOriginal(init);
        setForm({ ...init, new_password: '', force_password_change: true, reason: '' });
        setAccount(r.data.user_account);
        setCompanies(c.data);
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load employee'))
      .finally(() => setLoading(false));
  }, [open, employee]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const changed = Object.keys(original).filter(k => (form[k] ?? '') !== (original[k] ?? ''));
  const emailChanged = changed.includes('email');
  const dirty = changed.length > 0 || !!form.new_password;

  const submit = async () => {
    const payload = Object.fromEntries(changed.map(k => [k, form[k] === '' ? null : form[k]]));
    if (form.new_password) { payload.new_password = form.new_password; payload.force_password_change = form.force_password_change; }
    if (form.reason) payload.reason = form.reason;
    if ((emailChanged || form.new_password) && !window.confirm('Changing the login email or password signs this employee out on all devices. Continue?')) return;
    setSaving(true);
    try {
      const r = await employeeAdminAPI.fullEdit(employee.id, payload);
      toast.success(`Saved: ${r.data.changed_fields.join(', ') || 'no changes'}${r.data.sessions_revoked ? ' — employee signed out everywhere' : ''}`);
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="full-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog size={18} />Full Edit — {employee.first_name} {employee.last_name}</DialogTitle>
        </DialogHeader>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
          <div className="space-y-5">
            {SECTIONS.map(([title, fields]) => (
              <div key={title}>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map(([k, label, type]) => (
                    <div key={k}>
                      <Label className="text-xs">{label}</Label>
                      <Input type={type || 'text'} value={form[k] || ''} onChange={(e) => set(k, e.target.value)} data-testid={`full-edit-${k.replace(/_/g, '-')}`} className={changed.includes(k) ? 'border-amber-500' : ''} />
                    </div>
                  ))}
                  {title === 'Employment' && <>
                    <PickField label="Company *" value={form.company_id} onChange={(v) => set('company_id', v)} options={companies.map(c => [c.id, `${c.name} (${c.country})`])} testId="full-edit-company" />
                    <PickField label="Employment Type" value={form.employment_type} onChange={(v) => set('employment_type', v)} options={withCurrent(EMP_TYPES, original.employment_type).map(t => [t, t.replace('_', ' ')])} testId="full-edit-employment-type" />
                    <PickField label="Status" value={form.status} onChange={(v) => set('status', v)} options={withCurrent(STATUSES, original.status).map(s => [s, s.replace('_', ' ')])} testId="full-edit-status" />
                  </>}
                  {title === 'Identity & Login' && account?.has_account && (
                    <PickField label="System Role" value={form.role} onChange={(v) => set('role', v)} options={ROLES} testId="full-edit-role" />
                  )}
                </div>
                {title === 'Identity & Login' && emailChanged && (
                  <p className="mt-2 text-xs text-amber-600 flex items-center gap-1" data-testid="email-change-warning"><ShieldAlert size={14} />The employee will sign in with the new email and be signed out on all devices.</p>
                )}
              </div>
            ))}

            <div className="border-t pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Password</p>
              {account?.has_account ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Set New Password (min 8 chars, leave blank to keep)</Label>
                    <Input type="password" value={form.new_password || ''} onChange={(e) => set('new_password', e.target.value)} data-testid="full-edit-new-password" autoComplete="new-password" />
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <Switch checked={form.force_password_change} onCheckedChange={(v) => set('force_password_change', v)} data-testid="full-edit-force-change" />
                    <Label className="text-xs">Ask to change password at next login</Label>
                  </div>
                </div>
              ) : <p className="text-sm text-muted-foreground" data-testid="no-login-account-note">No login account yet — create one from the User Account card to set a role or password.</p>}
            </div>

            <div>
              <Label className="text-xs">Reason for change (kept in change log)</Label>
              <Input value={form.reason || ''} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Corrected email typo" data-testid="full-edit-reason" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="full-edit-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || loading || !dirty} data-testid="full-edit-save-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />}Save Changes{changed.length ? ` (${changed.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FullEditEmployeeDialog;
